import React from 'react'
import moment from 'moment'
import Link from 'next/link'
import dynamic from 'next/dynamic'

import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  CustomInput,
  Collapse
} from 'reactstrap'
import { SingleDatePicker } from 'react-dates'
import 'react-dates/initialize'

import Select from 'react-select'
import { useDropzone } from 'react-dropzone'

import { firstLetter } from 'common'
import PhoneInput from 'react-phone-input-2'
import ReactFlagsSelect from 'react-flags-select'
import Cropper from 'react-cropper'
import { FormContext } from './FormContext'
import GuruModal from './GuruModal'
import PasswordInput from './PasswordInput'
import useTranslation from '../hooks/UseTranslation'
import ColorPicker from './ColorPicker'
import LocationAndAddress from './LocationAndAddress'
import { ScriptContext } from '../contexts/ScriptContext'
import 'cropperjs/dist/cropper.css'
import imageCompression from 'browser-image-compression'
import classNames from 'classnames'

const AddressSearch = dynamic(() => import('./AddressSearch'), {
  ssr: false
})

moment.locale('fr-FR')
const DatePicker = (props) => {
  const [isFocused, setIsFocused] = React.useState(false)

  const returnYears = () => {
    const years = []
    for (let i = moment().year() - 100; i <= moment().year(); i++) {
      years.push(<option value={i}>{i}</option>)
    }
    return years
  }

  const renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div>
        <select
          value={month.month()}
          onChange={(e) => onMonthSelect(month, e.target.value)}
        >
          {moment.months().map((label, value) => (
            <option value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div>
        <select
          value={month.year()}
          onChange={(e) => onYearSelect(month, e.target.value)}
        >
          {returnYears()}
        </select>
      </div>
    </div>
  )

  return (
    <SingleDatePicker
      readOnly
      id='date'
      numberOfMonths={1}
      renderMonthElement={renderMonthElement}
      date={props.dateStart ? moment(props.dateStart) : null}
      block
      small
      renderNavPrevButton={() => null}
      renderNavNextButton={() => null}
      enableOutsideDays
      isOutsideRange={() => false}
      onDateChange={(dateStart) => props.onChange(dateStart)}
      focused={isFocused}
      onFocusChange={({ focused }) => setIsFocused(focused)}
      orientation='horizontal'
      showClearDate={false}
    />
  )
}


export const FormDropZone = (props) => {
  const { t } = useTranslation()
  const [cropper, setCropper] = React.useState()
  const [image, setImage] = React.useState(false)
  const [files, setFiles] = React.useState([])

  React.useEffect(() => {
    setFiles(props.files)
  }, [props.files])

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    maxFiles: 1,
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length == 0) return
      if (props.crop) {
        // make sure maxFiles stay 1 here!!
        const reader = new FileReader()
        reader.onload = () => {
          setImage(reader.result)
        }
        reader.readAsDataURL(acceptedFiles[0])
      } else {
        onSelected(acceptedFiles)
      }
    }
  })

  const toggle = () => {
    setImage(null)
  }

  const onSelected = (acceptedFiles) => {
    setImage(null)
    if(props.addOnSelect) {
      props.onSelected([...files, ...acceptedFiles])
    } else {
      props.onSelected(acceptedFiles)
    }
  }

  const getCropData = async () => {
    if (typeof cropper !== 'undefined') {
      imageCompression
        .canvasToFile(cropper.getCroppedCanvas())
        .then((f) => onSelected([f]))
    }
  }

  return (
    <div>
      <GuruModal
        isOpen={Boolean(image)}
        toggle={toggle}
        button={
          <Button color='primary' onClick={getCropData}>
            {t('profile_photo_upload')}
          </Button>
        }
      >
        {Boolean(image) && (
          <Cropper
            style={{ height: 400, width: '100%' }}
            // zoomTo={2}
            aspectRatio={props.aspectRatio}
            preview='.img-preview'
            src={image}
            viewMode={1}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            background={false}
            responsive
            autoCropArea={1}
            checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
            onInitialized={(instance) => {
              setCropper(instance)
            }}
            guides={false}
          />
        )}
      </GuruModal>
      <div {...getRootProps({ className: 'dropzone dz-clickable' })}>
        <input {...getInputProps()} />
        <div className='dz-message text-muted'>
          <p>{t(props.hint)}</p>
        </div>
      </div>
      <Row className='mt-4'>
        {files &&
          files.map((file, i) => (
            <div
              key={file.name}
              className='col-lg-4'
              style={{ maxWidth: '50%' }}
            >
              <div>
                <img
                  src={file.preview}
                  className='img-fluid rounded shadow mb-4'
                />
                <i
                  className='fas fa-times fa-fw text-muted'
                  style={{
                    cursor: 'pointer',
                    position: 'absolute',
                    top: 0,
                    right: -8
                  }}
                  onClick={() => props.onDelete(i)}
                />
              </div>
            </div>
          ))}
      </Row>
    </div>
  )
}

const ListingForm = (props) => {
  const { t } = useTranslation()
  const { data } = props
  const [formInputs, setFormInputs] = React.useContext(FormContext)
  const [calendarFocused, setIsCalendarFocused] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState(null)
  const [modal, setModal] = React.useState(false)
  const [validators, setValidators] = React.useState({})
  const [disabled, setDisabled] = React.useState(props.disabled)
  const [errorMsg, setErrorMsg] = React.useState('')
  const { placesLoaded } = React.useContext(ScriptContext)

  React.useEffect(() => {
    if (props.defaults) {
      Object.entries(props.defaults).forEach(([name, value]) => {
        formInputs[name] = value
      })
      setFormInputs({ ...formInputs })
    }
  }, [props.defaults])

  React.useEffect(() => {
    setDisabled(props.disabled)
  }, [props.disabled])

  const addValidator = (input, newValidators) => {
    const { name, validator, type, error } = input
    if (type == 'location_and_address') {
      newValidators[input.location.name] = (val) =>
        val ? true : input.location.error
      newValidators[input.address.name] = (val) =>
        val ? true : input.address.error
    } else if (typeof validator === 'function') {
      newValidators[name] = validator
    } else {
      if (validator == 'password') {
        newValidators[name] = (val) => (val?.length >= 8 ? true : error)
      }
      if (validator == 'positive') {
        newValidators[name] = (val) => (parseFloat(val) > 0 ? true : error)
      } else if (validator == 'nonempty') {
        if (type == 'checkboxes') {
          newValidators[name] = () => {
            let valid = false
            Object.keys(formInputs).map((key) => {
              if (key.startsWith(name) && formInputs[key] === true) {
                valid = true
              }
            })
            return valid || error
          }
        } else if (type == 'upload') {
          newValidators.files = (val) => (val?.length > 0 ? true : error)
        } else newValidators[name] = (val) => (val ? true : error)
      } else if (!validator) newValidators[name] = (val) => true
      else {
        console.warn(`unrecognized validator ${validator}`)
      }
    }
  }

  React.useEffect(() => {
    const newValidators = {}
    data.formBlocks.map((block) => {
      block.inputs.map((input) => {
        input.type == 'collapse'
          ? input.children.map((child) => addValidator(child, newValidators))
          : addValidator(input, newValidators)
      })
    })
    setValidators(newValidators)
  }, [props.data]) // need to update the validators if the blocks change

  React.useEffect(() => {
    let isValid = true
    let msg = ''
    Object.keys(validators).map((key) => {
      const subMsg = validators[key](formInputs[key], formInputs)
      let isSubValid = subMsg === true
      if (!isSubValid && !msg) {
        msg = subMsg
      }
      if (key == 'password' && formInputs[key] === null) {
        msg = 'signup_password_invalid_error'
        isSubValid = false
      }
      isValid = isValid && isSubValid
    })
    setErrorMsg(msg)
  }, [formInputs])

  const isValid = errorMsg === ''

  const onChange = (e) => {
    const { value } = e.target
    if (props.callback) props.callback(e.target.name, value)
    setFormInputs({ ...formInputs, [e.target.name]: value })
  }

  const onPasswordChange = (valid, password) => {
    setFormInputs({ ...formInputs, password: valid ? password : null })
  }

  const onCheckboxChange = (e) => {
    const { value } = e.target
    setFormInputs({ ...formInputs, [e.target.id]: !value })
  }

  const onSelectChange = (name, e, onChange) => {
    onChange && onChange(e)
    setFormInputs({ ...formInputs, [name]: e })
  }

  const onButtonDecrease = (e, name) => {
    const value = parseInt(e.target.nextElementSibling.value, 10)
    setFormInputs({ ...formInputs, [name]: value - 1 })
  }
  const onButtonIncrease = (e, name) => {
    const value = parseInt(e.target.previousElementSibling.value, 10)

    cosetFormInputs({ ...formInputs, [name]: value + 1 })
  }
  const onDateChange = (name, date) => {
    setSelectedDate(date)
    setFormInputs({ ...formInputs, [name]: date })
  }

  const getInput = (input, index) => (
    <React.Fragment key={index}>
      {(input.type === 'text' ||
        input.type === 'email' ||
        input.type === 'time' ||
        input.type === 'number') && (
        <FormGroup style={input.display ? { display: input.display } : {}}>
          <Label className='form-label' for={input.name}>
            {t(input.label)}
          </Label>
          <Input
            type={input.type}
            input={input.name}
            autocomplete={input.autocomplete}
            name={input.name}
            id={input.name}
            placeholder={firstLetter(t(input.placeholder))}
            value={formInputs[input.name] || ''}
            onChange={(e) => onChange(e)}
          />

          {input.text && (
            <small id='help' className='form-text text-muted'>
              {t(input.text)}
            </small>
          )}
        </FormGroup>
      )}
      {input.type === 'phone' && (
        <FormGroup>
          <Label for={input.name} className='form-label'>
            {t(input.label)}
          </Label>
          <PhoneInput
            specialLabel={null}
            country='fr'
            name={input.name}
            id={input.name}
            value={formInputs[input.name] || ''}
            onChange={(phone) =>
              setFormInputs({ ...formInputs, [input.name]: phone })
            }
          />
        </FormGroup>
      )}
      {input.type === 'password' && (
        <PasswordInput
          label1={t('password')}
          label2={t('password_confirm')}
          placeholder1={firstLetter(t('password'))}
          placeholder2={firstLetter(t('password_confirm'))}
          autocomplete={input.autocomplete}
          onChange={onPasswordChange}
        />
      )}
      {input.type === 'flag' && (
        <FormGroup>
          <Label for={input.name} className='form-label'>
            {t(input.label)}
          </Label>
          <ReactFlagsSelect
            searchable={false}
            selectButtonClassName='form-control mr-2 text-sm text-muted'
            selected={formInputs[input.name]}
            onSelect={(code) =>
              setFormInputs({ ...formInputs, [input.name]: code })
            }
          />
        </FormGroup>
      )}
      {input.type === 'date' && (
        <FormGroup>
          <Label for={input.name} className='form-label'>
            {t(input.label)}
          </Label>
          <DatePicker
            dateStart={formInputs[input.name]}
            onChange={(dateStart) =>
              setFormInputs({ ...formInputs, [input.name]: dateStart })
            }
          />
        </FormGroup>
      )}
      {input.type === 'textarea' && (
        <FormGroup className='mb-0'>
          <Label className='form-label' for={input.name}>
            {t(input.label)}
          </Label>
          <Input
            type={input.type}
            rows={input.rows ?? '5'}
            input={input.name}
            name={input.name}
            placeholder={firstLetter(t(input.placeholder))}
            id={input.name}
            value={formInputs[input.name] || ''}
            onChange={(e) => onChange(e)}
            aria-describedby={input.helpId}
          />
          <small id={input.helpId} className='form-text text-muted mt-2'>
            {input.help}
          </small>

          {input.text && (
            <small id='help' className='form-text text-muted'>
              {t(input.text)}
            </small>
          )}
        </FormGroup>
      )}
      {input.type === 'select' && (
        <FormGroup>
          <Label className='form-label' for={input.name}>
            {t(input.label)}
          </Label>
          <Select
            isSearchable={false}
            id={input.name}
            name={input.name}
            options={input.options}
            isDisabled={input.disabled}
            isMulti={input.isMulti === true}
            isGrouped={Boolean(input.formatGroupLabel)}
            className='selectpicker'
            classNamePrefix='selectpicker'
            placeholder={firstLetter(t('default_select_placeholder'))}
            formatGroupLabel={input.formatGroupLabel}
            value={formInputs[input.name] || ''}
            defaultValue={formInputs[input.name] || ''}
            onChange={(e) => onSelectChange(input.name, e, input.onChange)}
          />
          {input.text && (
            <small id='help' className='form-text text-muted'>
              {t(input.text)}
            </small>
          )}
        </FormGroup>
      )}
      {input.type === 'radios' && (
        <FormGroup>
          <Label className='form-label'>{t(input.label)}</Label>
          <ul className='list-inline mb-0'>
            <li key='radio_private' className='list-inline-item'>
              {input.radios.map((radio) => (
                <CustomInput
                  key={radio.label}
                  type='radio'
                  id={radio.id}
                  name={radio.name}
                  value={radio.id}
                  onChange={(e) => onChange(e)}
                  checked={formInputs[radio.name] === radio.id}
                  label={firstLetter(t(radio.label))}
                  className='text-muted'
                />
              ))}
            </li>
          </ul>
        </FormGroup>
      )}
      {input.type === 'form-group' && (
        <Row>
          {input.inputs.map((input) => (
            <Col md={input.col} key={input.name}>
              <FormGroup>
                <Label for={input.name} className='form-label'>
                  {t(input.label)}
                </Label>
                <Input
                  name={input.name}
                  id={input.name}
                  value={formInputs[input.name] || ''}
                  onChange={(e) => onChange(e)}
                />
              </FormGroup>
            </Col>
          ))}
        </Row>
      )}
      {input.type === 'buttons' && (
        <Row>
          {input.buttons.map((button) => (
            <Col lg='4' key={button.name}>
              <Label className='form-label'>{button.label}</Label>
              <div className='d-flex align-items-center'>
                <Button
                  color='items'
                  className='btn-item-decrease'
                  onClick={(e) => onButtonDecrease(e, button.name)}
                >
                  -
                </Button>
                <Input
                  name={button.name}
                  value={formInputs[button.name] || 1}
                  disabled
                  className='input-items'
                />
                <Button
                  color='items'
                  className='btn-item-increase'
                  onClick={(e) => onButtonIncrease(e, button.name)}
                >
                  +
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      )}
      {input.type === 'checkboxes' && (
        <FormGroup>
          <Label className='form-label'>{t(input.label)}</Label>
          <ul className='list-inline mb-0'>
            {input.checkboxes.map((checkbox) => (
              <li key={checkbox.id} className='list-inline-item'>
                <CustomInput
                  type='checkbox'
                  id={checkbox.id}
                  name={checkbox.name}
                  value={formInputs[checkbox.id] || ''}
                  onChange={(e) => onCheckboxChange(e)}
                  label={t(checkbox.label)}
                  className='text-muted'
                />
              </li>
            ))}
          </ul>
        </FormGroup>
      )}
      {input.type == 'color' && (
        <FormGroup>
          <Label className='form-label' for={input.name}>
            {t(input.label)}
          </Label>
          <ColorPicker
            name={input.name}
            id={input.name}
            color={formInputs[input.name] || ''}
            onChange={(color) => {
              setFormInputs({ ...formInputs, [input.name]: color })
            }}
          />
          {input.text && (
            <small id='help' className='form-text text-muted'>
              {t(input.text)}
            </small>
          )}
        </FormGroup>
      )}
      {input.type === 'address' && placesLoaded && (
        <AddressSearch
          label={t(input.label)}
          value={formInputs[input.name] || ''}
          disabled={input.disabled}
          cityLatLng={{ lat: input.lat, lng: input.lng }}
          onSelected={(address) =>
            setFormInputs({ ...formInputs, [input.name]: address })
          }
        />
      )}
      {input.type === 'location_and_address' && (
        <LocationAndAddress
          disabled={input.disabled}
          locationProps={input.location}
          addressProps={input.address}
          selectedLocation={formInputs[input.location.name] || null}
          selectedAddress={formInputs[input.address.name] || null}
          onSelectedLocation={(e) => onSelectChange(input.location.name, e)}
          onSelectedAddress={(address) =>
            setFormInputs({ ...formInputs, [input.address.name]: address })
          }
        />
      )}
      {input.type === 'upload' && (
        <FormGroup>
          {input.label && (
            <Label className='form-label'>{t(input.label)}</Label>
          )}
          <FormDropZone
            crop={input.crop}
            files={formInputs.files}
            hint={input.hint}
            aspectRatio={input.aspectRatio}
            onDelete={(index) => {
              const newFiles = []
              formInputs.files.map((f, j) => index != j && newFiles.push(f))
              setFormInputs({ ...formInputs, files: newFiles })
            }}
            onSelected={(acceptedFiles) =>
              setFormInputs({
                ...formInputs,
                files: acceptedFiles.map((file) =>
                  Object.assign(file, {
                    preview: URL.createObjectURL(file)
                  })
                )
              })
            }
          />
        </FormGroup>
      )}
    </React.Fragment>
  )

  return (
    <Form>
      <GuruModal
        isOpen={modal}
        toggle={() => setModal(false)}
        title={t('modal_title_oops')}
      >
        {t(errorMsg)}
      </GuruModal>
      {data.formBlocks.map((block) => (
        <Row
          className={`form-block ${props.className ?? ''}`}
          key={block.title}
        >
          {!props.hideLeft && (
            <Col lg='4'>
              <h4
                className='text-first-letter'
                dangerouslySetInnerHTML={{ __html: t(block.title) }}
              />
              <div
                className='text-muted text-sm'
                dangerouslySetInnerHTML={{ __html: t(block.content) }}
              />
            </Col>
          )}
          <Col
            lg={props.hideLeft ? '12' : '7'}
            className={props.hideLeft ? '' : 'ml-auto'}
          >
            {block.inputs.map((input, index) => {
              if (input.type == 'collapse') {
                return (
                  <Collapse key={input.key} isOpen={input.is_open}>
                    {input.children.map(getInput)}
                  </Collapse>
                )
              }
              if (input.type == 'row') {
                return (
                  <Row style={input.display ? { display: input.display } : {}}>
                    {input.children.map((child, i) => (
                      <Col className='lg-2 xs-2' key={i}>
                        {getInput(child)}
                      </Col>
                    ))}
                  </Row>
                )
              }
              return getInput(input, index)
            })}
          </Col>
        </Row>
      ))}
      <Row className='form-block flex-column flex-sm-row'>
        <Col className='text-center text-sm-left'>
          {props.prevStep &&
            (typeof props.prevStep === 'string' ? (
              <Link href={props.prevStep} passHref>
                <Button color='link' className='text-muted'>
                  <i className='fa-chevron-left fa mr-2' />
                  {t('back')}
                </Button>
              </Link>
            ) : (
              <Button
                onClick={props.prevStep}
                color='link'
                className='text-muted'
              >
                <i className='fa-chevron-left fa mr-2' />
                {t('back')}
              </Button>
            ))}
        </Col>
        <Col className='text-center text-sm-right'>
          {props.nextStep && (
            <Link href={isValid ? props.nextStep : '#'} passHref>
              <Button
                color='primary'
                className='px-3'
                onClick={() => setModal(!isValid)}
              >
                {t('listing_form_next_step')}
                <i className='fa-chevron-right fa ml-2' />
              </Button>
            </Link>
          )}
          {props.finish &&
            (typeof props.finish === 'string' ||
            typeof props.finish === 'object' ? (
              <Link href={isValid ? props.finish : '#'} passHref>
                <Button
                  color='primary'
                  className='px-3'
                  disabled={disabled}
                  onClick={() => setModal(!isValid)}
                >
                  {t('listing_form_finish')}
                  <i className='fa-chevron-right fa ml-2' />
                </Button>
              </Link>
            ) : (
              <Button
                color='primary'
                className='px-3'
                disabled={disabled}
                onClick={() => {
                  if (isValid) {
                    props.finish(formInputs)
                  } else {
                    setModal(!isValid)
                  }
                }}
              >
                {t('listing_form_finish')}
                <i className='fa-chevron-right fa ml-2' />
              </Button>
            ))}
        </Col>
      </Row>
    </Form>
  )
}

export default ListingForm
